import { useState, useEffect, useRef } from 'react';
import { read_cookie } from "sfcookies";
import { hostUrl, UseUrlManagerCleanUrl } from "../../../../utils/url-manager";
import { NotificationType, useNotify } from "react-admin";

/**
 * How many emails a page holds. This is the backend's number, not a display choice:
 * `EmailMessageRepository::loadPaginatedEmailsForNok()` sets `$limit = 15`. HQ-02 was this
 * value being 5 here while the API returned 15, so the panel offered three times as many
 * pages as exist.
 */
export const EMAILS_PER_PAGE = 15;

/**
 * How long to wait after the last keystroke before asking the API. The filter box updates
 * state on every character, and HQ-01's fix puts the filter in the effect's dependencies, so
 * without this a ten-letter search would be ten requests.
 */
export const FILTER_DEBOUNCE_MS = 300;

/**
 * The highest page number that has anything on it.
 */
export function lastPageFor(totalItems: number): number {
    return Math.max(1, Math.ceil(totalItems / EMAILS_PER_PAGE));
}

export const useNokEditEmails = (nokWebsiteId: string, activePanel: string, filterValue: string) => {
    const [emails, setEmails] = useState([]);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [totalItems, setTotalItems] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<boolean>(false);
    const [debouncedFilter, setDebouncedFilter] = useState<string>(filterValue);
    const notify = useNotify();

    // Which request is the current one. A per-keystroke search can have three in flight and
    // fetch answers in whatever order the network decides, so without this the panel can
    // settle on the results of a search the admin has already typed past.
    const requestId = useRef<number>(0);

    // What the last fetch was for. Changing the Nok, the direction or the filter has to take
    // the panel back to page one; comparing against this tells a page change from a query
    // change without firing a request for the page we are about to leave.
    const lastQuery = useRef<string>('');

    useEffect((): (() => void) => {
        const timer = setTimeout((): void => {
            setDebouncedFilter(filterValue);
        }, FILTER_DEBOUNCE_MS);

        return (): void => clearTimeout(timer);
    }, [filterValue]);

    function loadData(page: number, panel: string, filter: string): void {
        let url: string  = hostUrl + 'hq/nok/email/emails?id=' +
            nokWebsiteId + '&pageNumber=' + page + '&direction=' + panel + '&filter=' + filter;
        url = UseUrlManagerCleanUrl({ url: url });

        let token: string = read_cookie('auth');
        const request: Request = new Request(url, {
            method: 'GET',
            headers: new Headers({
                'Content-Type' : 'application/json',
                'token' : token
            })
        });
        requestId.current = requestId.current + 1;
        const thisRequest: number = requestId.current;

        fetch(request)
            .then( (response: Response) => {
                return response.json();
            })
            .then( (data: any) => {
                if (thisRequest !== requestId.current) {
                    return; // a newer request has gone out; this answer is stale
                }
                setLoading(false);
                if (data !== undefined && data['message'] === 'success') {
                    const datum = data['data'];
                    setEmails(datum['items']);
                    setTotalItems(datum['totalItems']);
                } else {
                    setEmails([]);
                    setTotalItems(0);
                    let msg = data['message'] ?? 'Something has gone wrong!'
                    let type: NotificationType = 'error';
                    if (msg.toLowerCase().includes('no items found')) {
                        type = 'warning';
                    }
                    notify(msg, { type: type });
                }
            })
            .catch( (err: Error) => {
                if (thisRequest !== requestId.current) {
                    return;
                }
                console.log(err);
                setError(true);
                setLoading(false);
            })
    }

    function nextPage(): void {
        const currentPage: number = pageNumber + 1;
        // HQ-02. This was `totalItems / 5` with a hand-rolled remainder bump and a `<`
        // comparison, which both allowed three times too many pages and made the last page
        // unreachable whenever the total divided exactly.
        if (currentPage <= lastPageFor(totalItems)) {
            setPageNumber(currentPage);
        }
    }

    function previousPage(): void {
        let currentPage: number = pageNumber;
        currentPage = currentPage - 1;
        if (currentPage >= 1) {
            setPageNumber(currentPage);
        }
    }

    // HQ-01. This used to end `}, [nokWebsiteId])`, so the request ran once per Nok and never
    // again: the next and previous buttons moved a counter, and changing the direction or the
    // filter changed nothing the admin could see.
    useEffect( (): void => {
        if (nokWebsiteId === undefined) {
            return;
        }
        const query: string = [nokWebsiteId, activePanel, debouncedFilter].join(' ');
        if (query !== lastQuery.current) {
            lastQuery.current = query;
            if (pageNumber !== 1) {
                // A new query starts at page one. Resetting rather than fetching here is what
                // stops a request going out for a page we are leaving anyway.
                setPageNumber(1);
                return;
            }
        }
        setLoading(true);
        loadData(pageNumber, activePanel, debouncedFilter);
    }, [nokWebsiteId, pageNumber, activePanel, debouncedFilter] );


    return { emails, loading, error, totalItems, pageNumber, nextPage, previousPage };
}
